/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import StatusPicker from '@components/form/StatusPicker';
import SociollaText from '@components/sociolla/SociollaText';
import StatusBadge from '@components/global/StatusBadge';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import MarketTypeSearch from '@components/form/search/MarketTypeSearch';
import * as Layout from '@components/screen/Layout';
import { showPopup } from '@/store/modal';
import { setTempData } from '@/store/global';
import { exportCsvApi } from '@global/helpers';
import { filterAdded, paramsReseted } from '@/store/params';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetSalesTeamListQuery,
  useGetSalesTeamCountQuery,
  useGetSalesTeamInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  name: {
    head: 'Name',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  market_type_code: { head: 'Market Type', body: 'market_type.code' },
  is_active: { head: 'Is Active', body: 'is_active', type: 'status_active' },
};

// Search box writes `name` (→ $regex); market_type.code passes through.
// (is_show_all is baked into the resource's defaultFilter.)
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  if (raw['market_type.code']) {
    filter['market_type.code'] = raw['market_type.code'];
  }
  return filter;
};

// Display row (RTK cache frozen → new object): uppercase the market type code.
const mapRow = (item) => {
  const next = { ...item, id: item._id };
  if (item?.market_type?.code) {
    next.market_type = {
      ...item.market_type,
      code: item.market_type.code.toUpperCase(),
    };
  }
  return next;
};

const SalesScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const marketTypes = useSelector((state) => state.resources.marketTypes);
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state ──
  const [draftFilter, setDraftFilter] = useState({});
  // Full selected market-type object (has `id`) — MarketTypeSearch/SelectBox
  // matches the checked option by `id`, so a bare {code} won't tick the radio.
  const [marketTypeValue, setMarketTypeValue] = useState('');

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetSalesTeamListQuery,
    countHook: useGetSalesTeamCountQuery,
    infiniteHook: useGetSalesTeamInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── handlers ──
  const gotToDetail = ({ data }) => {
    openChild('SalesTeamDetailScreen', {
      titleScreen: data.name,
      name: data.name,
      id: data._id,
    });
  };

  const onExport = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      exportCsvApi('sales-teams', { is_deleted: false });
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const onSelectMarketType = (value) => {
    if (value && value.code) {
      setMarketTypeValue(value);
      dispatch(
        filterAdded({
          key: scope,
          filter: { ...params.filter, 'market_type.code': value.code },
        }),
      );
    }
  };

  // ── effects ──
  // Mobile export (navigator ⋯ menu) reads the visible ids from global tempData.
  useEffect(() => {
    if (!isWeb) {
      dispatch(setTempData(rows.map((item) => item.id)));
    }
  }, [rows, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setTempData([]));
    };
  }, [dispatch]);

  // Drop the cached market-type object if the applied filter loses it (reset).
  useEffect(() => {
    if (!params.filter['market_type.code']) {
      setMarketTypeValue('');
    }
  }, [params.filter]);

  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      setDraftFilter(params.filter || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Sales Team">
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Button
                type="primary"
                rounded={true}
                buttonStyle={{ width: 100 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    Export
                  </SociollaText>
                }
                onPress={() => onExport()}
              />
            </View>
          </Layout.Title>

          <Table
            filterName={scope}
            name="sales-teams"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasCallback={true}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            showFilter={false}
            searchKey="name"
            searchPlaceholder="Search by sales team name..."
            onClickRow={(val) => gotToDetail(val)}
            searchValue={searchValue}
            moreInlineFilter={
              <View>
                <MarketTypeSearch
                  hideLabel={true}
                  isDropdown
                  inputContainerStyle={{ backgroundColor: Colors.white }}
                  value={
                    marketTypeValue ||
                    (params.filter['market_type.code']
                      ? { code: params.filter['market_type.code'] }
                      : '')
                  }
                  onChange={onSelectMarketType}
                />
              </View>
            }
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          placeholder="Search by sales team name"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              onPress={() => gotToDetail({ data: data })}>
              <SociollaText type="medium">{data.name}</SociollaText>
              <SociollaText style={{ fontSize: 12 }}>
                {data?.market_type?.code ? data.market_type.code : '-'}
              </SociollaText>
              <View
                style={[
                  styles.salesItemSection,
                  { marginBottom: 10, textAlign: 'center' },
                ]}>
                <StatusBadge status={data.is_active ? 'active' : 'in_active'} />
              </View>
            </Layout.ItemMobile>
          )}
        />
      )}
      <Layout.Filter
        name="sales-teams"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem lastItem>
          <StatusPicker
            label="Market Type"
            options={marketTypes}
            value={(marketTypes || []).find(
              (m) => m.code === draftFilter['market_type.code'],
            )}
            trackBy="code"
            labelKey="code"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, 'market_type.code': val.code }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  salesItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

export default SalesScreen;
